import { useContext, useState, useEffect } from 'react'
import _ from 'lodash'
import colorconvert from 'color-convert'

import { PassContext } from "../pass-context"
import dataSelector from './data-selector'
import PopupEditor from './popup-editor'

const PassListItem = () => {

  const { passType, subType, classData, objectData, instanceData, scannerInstanceData } = useContext(PassContext)

  const [imageSrc, setImageSrc] = useState(null)

  const bgColor = getBackgroundColour({ objectData, classData })

  const passData = getPassTypeData({ passType, subType, classData, objectData, instanceData, scannerInstanceData })

  const logoUrl = dataSelector({ objectData: passData.logoSource === "class" ? classData : objectData, instanceData, scannerInstanceData, path: passData.logo })
  const wideLogoUrl = dataSelector({ objectData: passData.logoSource === "class" ? classData : objectData, instanceData, scannerInstanceData, path: passData.wideLogo })

  const logoEditorSources = {
    source: passData.logoSource,
    logo: {
      path: passData.logo,
      value: logoUrl
    },
    wideLogo: {
      path: passData.wideLogo,
      value: wideLogoUrl
    }
  }

  useEffect(() => {
    if (!logoUrl) return
    
    const img = new Image()
    img.crossOrigin = "anonymous"          // important for canvas/color picking
    img.onload = () => {
      setImageSrc(logoUrl)
    }
    img.onerror = () => setImageSrc(null)
    img.src = logoUrl                   // set after crossOrigin
  }, [logoUrl])

  return (
    <div className="list-item" style={{ backgroundColor: bgColor, color: "black" }}>
      <table width="100%">
        <tbody>
          <tr>
            <td valign="middle" width={75}>
              <PopupEditor sources={logoEditorSources} type="logo" title="Logo Image">
                <div className="image" style={{ position: "relative" }}>
                  <div style={{ position: "absolute", top: -35, height: 70, width: 70 }} className="editable">
                    <img src={imageSrc} crossOrigin="anonymous" />
                  </div>
                </div>
              </PopupEditor>
            </td>
            <td valign="middle">
              <div className={'title' + (passData.titleEditable ? " editable" : "")}>
                <PopupEditor title={passData.titleLabel} sources={[passData.titleSource]} path={passData.titlePath} disabled={!passData.titleEditable} required={passData.titleRequired}>
                  <div style={{ minWidth: 100, overflow: "wrap" }}>{passData.titleValue}</div>
                </PopupEditor>
              </div>
              <div className={'body' + (passData.bodyEditable ? " editable" : "")}>
                <PopupEditor title={passData.bodyLabel} sources={[passData.bodySource]} path={passData.bodyPath} disabled={!passData.bodyEditable} required={passData.bodyRequired}>
                  <div style={{ minWidth: 100, overflow: "wrap" }}>{passData.bodyValue}</div>
                </PopupEditor>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default PassListItem

const getBackgroundColour = ({ objectData, classData }) => {
  // TODO Support colours in instance data
  let bgBaseColor = objectData.hexBackgroundColor ? objectData.hexBackgroundColor : classData.hexBackgroundColor

  if (!bgBaseColor) bgBaseColor = "#ffffff"

  const hsl = colorconvert.hex.hsl(bgBaseColor)
  hsl[2] = Math.max(hsl[2], 90) // Lighten the colour
  return "#" + colorconvert.hsl.hex(hsl)
}

const getPassTypeData = ({ passType, subType, classData, objectData, instanceData, scannerInstanceData }) => {
  switch (passType) {
    case "generic": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "object",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Title",
      titlePath: "cardTitle.defaultValue.value",
      titleSource: "object",
      titleValue: dataSelector({ objectData: objectData, instanceData, scannerInstanceData, path: "cardTitle.defaultValue.value" }),
      bodyEditable: true,
      bodyRequired: true,
      bodyLabel: "Header",
      bodyPath: "header.defaultValue.value",
      bodySource: "object",
      bodyValue: dataSelector({ objectData: objectData, instanceData, scannerInstanceData, path: "header.defaultValue.value" }),
    }

    case "boardingPass": return {
      logo: "flightHeader.carrier.airlineLogo.sourceUri.uri",
      wideLogo: "flightHeader.carrier.airlineWideLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: false,
      titleLabel: "",
      titlePath: "",
      titleSource: "class",
      titleValue: "",
      bodyEditable: false,
      bodyLabel: "",
      bodyPath: "",
      bodySource: "class",
      bodyValue: ""
    }

    case "transit": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "object",
      titleEditable: false,
      titleLabel: "Journey",
      titlePath: "",
      titleValue: getTransitTitle({ subType, instanceData }),
      titleSource: "object",
      bodyEditable: false,
      bodyLabel: "Departure Date",
      bodyPath: "",
      bodySource: "object",
      bodyValue: getTransitBody({ subType, instanceData })
    }

    case "offer": return {
      logo: "titleImage.sourceUri.uri",
      wideLogo: "wideTitleImage.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Issuer Name",
      titlePath: "issuerName",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "issuerName" }),
      bodyEditable: true,
      bodyRequired: false,
      bodyLabel: "Title",
      bodyPath: "title",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "title" })
    }

    case "giftCard": return {
      logo: "programLogo.sourceUri.uri",
      wideLogo: "wideProgramLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Balance",
      titlePath: "balance.micros",
      titleSource: "object",
      titleValue: getGiftCardBody({ objectData, instanceData, scannerInstanceData }),
      bodyLabel: "Issuer Name",
      bodyPath: "issuerName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "issuerName" }),
      bodyEditable: true,
      bodyRequired: true,
    }
    case "loyalty": return {
      logo: "programLogo.sourceUri.uri",
      wideLogo: "wideProgramLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Issuer Name",
      titlePath: "issuerName",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "issuerName" }),
      bodyEditable: true,
      bodyRequired: true,
      bodyLabel: "Program Name",
      bodyPath: "programName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "programName" }),
    }
    case "eventTicket": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Event Name",
      titlePath: "eventName.defaultValue.value",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "eventName.defaultValue.value" }),
      bodyLabel: "Issuer Name",
      bodyEditable: true,
      bodyRequired: true,
      bodyPath: "issuerName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, instanceData, scannerInstanceData, path: "issuerName" }),
    }
  }
}

const getTransitTitle = ({ subType, instanceData }) => {
  switch (subType) {
    case "RAIL": {
      const origin = instanceData.journey.origin.code
      const destination = instanceData.journey.destination.code
      return `${origin} to ${destination}`
    }
  }
}

const getTransitBody = ({ subType, instanceData }) => {
  switch (subType) {
    case "RAIL": {
      return instanceData.journey.depart.date
    }
  }
}

const getGiftCardBody = ({ objectData, instanceData, scannerInstanceData }) => {
  const resolvedBalance = dataSelector({ objectData, instanceData, scannerInstanceData, path: "balance" })
  const microsString = Number(resolvedBalance.micros.toString().replace(/[^0-9.-]+/g, ""));
  return "Gift Card: " + microsString.toLocaleString('en-US', { style: 'currency', currency: resolvedBalance.currencyCode, minimumFractionDigits: 0 })
}